import React, { useRef, useState, useEffect } from "react";
import SignaturePad from "react-signature-canvas";
import { Card, Button, Typography, Empty, Row, Col, message } from "antd";
import { useSelector } from "react-redux";
import {
  PlusOutlined,
  SaveOutlined,
  ClearOutlined,
  EditOutlined,
  UndoOutlined,
  RedoOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import "./style-firma.css";
import config from "../../config";

const baseUrl = config.baseUrl;
const { Title } = Typography;

const FirmaCanvas = ({
  name,
  label = "Firma Digital",
  form,
  formLoaded,
  disabledCambioFirma,
}) => {
  const sigPad = useRef(null);
  const canvasTempRef = useRef(null);
  const fileInputRef = useRef(null);
  const firmaOriginalRef = useRef(null);

  const [firma, setFirma] = useState(null);
  const [mostrarPad, setMostrarPad] = useState(false);
  const [disableEdit, setDisableEdit] = useState(disabledCambioFirma || false);
  const [validarFirma, setValidarFirma] = useState(false);
  const [rotacion, setRotacion] = useState(0);
  const usernameFromState = useSelector((state) => state.auth.user);

  const convertirURLaBase64 = async (url) => {
    const urlConTimestamp = `${url}?t=${Date.now()}`; // Agrega el timestamp para evitar caché
    const respuesta = await fetch(urlConTimestamp, { mode: "cors" });

    const blob = await respuesta.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const firmaModificada = (nuevaFirma) => {
    return nuevaFirma !== firmaOriginalRef.current;
  };

  useEffect(() => {
    const cargarFirma = async () => {
      const valorInicial = form.getFieldValue(name);
      if (valorInicial) {
        try {
          let base64;

          if (/^data:image\/[a-zA-Z]+;base64,/.test(valorInicial)) {
            // 👉 Ya viene en formato base64
            base64 = valorInicial;
          } else if (/^https?:\/\//.test(valorInicial)) {
            // 👉 Es una URL absoluta
            base64 = await convertirURLaBase64(valorInicial);
          } else {
            // 👉 Es ruta relativa → concatenamos con baseUrl
            const urlCompleta = baseUrl + valorInicial;
            base64 = await convertirURLaBase64(urlCompleta);
          }

          setFirma(base64);
          firmaOriginalRef.current = base64;
          setMostrarPad(false);
          setValidarFirma(true);
          form.setFieldValue(name, base64);
        } catch (error) {
          console.error("Error al convertir URL a Base64:", error);
          message.error("No se pudo cargar la firma.");
        }
      } else if (disableEdit) {
        setMostrarPad(false);
        setFirma(true);
        setValidarFirma(false);
      }
    };

    if (formLoaded) {
      cargarFirma();
    }
  }, [form, name, formLoaded, disableEdit]);

  const limpiarFirma = () => {
    sigPad.current.clear();
    setFirma(null);
    form.setFieldValue(name, null);
  };

  const guardarImagenRotada = (angulo) => {
    const canvasTemp = canvasTempRef.current;
    const ctx = canvasTemp.getContext("2d");
    const img = new Image();
    img.src = firma;

    img.onload = () => {
      if (angulo % 180 !== 0) {
        canvasTemp.width = img.height;
        canvasTemp.height = img.width;
      } else {
        canvasTemp.width = img.width;
        canvasTemp.height = img.height;
      }

      ctx.clearRect(0, 0, canvasTemp.width, canvasTemp.height);
      ctx.save();
      ctx.translate(canvasTemp.width / 2, canvasTemp.height / 2);
      ctx.rotate((angulo * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      ctx.restore();

      const firmaRotada = canvasTemp.toDataURL();
      if (firmaModificada(firmaRotada)) {
        setFirma(firmaRotada);
        form.setFieldValue(name, firmaRotada);
      } else {
        form.setFieldValue(name, null);
      }
    };
  };

  const guardarFirma = () => {
    if (!sigPad.current.isEmpty()) {
      const firmaDataURL = sigPad.current.toDataURL();
      if (firmaModificada(firmaDataURL)) {
        setFirma(firmaDataURL);
        form.setFieldValue(name, firmaDataURL);
      } else {
        form.setFieldValue(name, null); // Firma igual → retornar null
      }
      setValidarFirma(true);
      setMostrarPad(false);
    } else {
      message.warning("La firma está vacía.");
    }
  };

  const iniciarNuevaFirma = () => {
    setMostrarPad(true);
    setFirma(null);
    form.setFieldValue(name, null);
  };

  const rotarIzquierda = () => {
    setRotacion((prevRotacion) => {
      const nuevaRotacion = prevRotacion - 90;
      guardarImagenRotada(nuevaRotacion);
      return nuevaRotacion;
    });
  };

  const rotarDerecha = () => {
    setRotacion((prevRotacion) => {
      const nuevaRotacion = prevRotacion + 90;
      guardarImagenRotada(nuevaRotacion);
      return nuevaRotacion;
    });
  };

  const manejarClickCargarFirma = () => {
    fileInputRef.current?.click();
  };

  const cargarFirmaDesdeArchivo = (e) => {
    const archivo = e.target.files[0];
    if (archivo) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const dataURL = evt.target.result;
        if (firmaModificada(dataURL)) {
          setFirma(dataURL);
          form.setFieldValue(name, dataURL);
        } else {
          form.setFieldValue(name, null);
        }
        setMostrarPad(false);
      };
      reader.readAsDataURL(archivo);
    }
  };

  return (
    <Card className="contenedor-firma">
      <canvas ref={canvasTempRef} style={{ display: "none" }} />

      {!mostrarPad && !firma && (
        <Row justify="center" style={{ marginBottom: 24 }}>
          <Col>
            <Empty description="No hay firma registrada" />
            <Button
              type="primary"
              onClick={iniciarNuevaFirma}
              icon={<PlusOutlined />}
              size="large"
              style={{ marginTop: 16 }}
            >
              Agregar Firma
            </Button>
          </Col>
        </Row>
      )}

      {mostrarPad && (
        <>
          <div className="area-firma">
            <SignaturePad
              ref={sigPad}
              canvasProps={{
                className: "firma-canvas",
                style: { width: "100%", height: "100%" },
              }}
              options={{
                penColor: "black",
                throttle: 16,
                velocityFilterWeight: 0.7,
              }}
            />
          </div>

          <div
            style={{
              marginTop: 24,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
            }}
          >
            {usernameFromState.username === "admin" && (
              <div>
                <Button
                  icon={<UploadOutlined />}
                  size="large"
                  type="dashed"
                  onClick={manejarClickCargarFirma}
                >
                  Cargar Firma
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={cargarFirmaDesdeArchivo}
                  style={{ display: "none" }}
                />
              </div>
            )}

            <div style={{ display: "flex", gap: 16 }}>
              <Button
                onClick={limpiarFirma}
                icon={<ClearOutlined />}
                size="large"
              >
                Limpiar
              </Button>
              <Button
                onClick={guardarFirma}
                type="primary"
                icon={<SaveOutlined />}
                size="large"
              >
                Guardar Firma
              </Button>
            </div>
          </div>
        </>
      )}

      {!mostrarPad && firma && (
        <>
          {!disableEdit && (
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <Title level={5}>Firma registrada:</Title>
            </div>
          )}

          <div className="imagen-firma-contenedor">
            <img src={firma} alt="Firma" className="imagen-firma" />
          </div>

          <div className="botones-rotacion">
            <Button
              onClick={rotarIzquierda}
              icon={<UndoOutlined />}
              size="large"
            >
              Rotar Izquierda
            </Button>
            <Button onClick={rotarDerecha} icon={<RedoOutlined />} size="large">
              Rotar Derecha
            </Button>
          </div>

          {!disableEdit && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 24,
              }}
            >
              <Button
                type="primary"
                onClick={iniciarNuevaFirma}
                icon={<EditOutlined />}
                size="large"
              >
                Cambiar Firma
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default FirmaCanvas;
